// utils/booking/locales.ts
//
// The public booking page's locale set and resolution (plan
// 2026-09-08-booking-public-page-localisation, contract C1). Pure: the
// composables (useBookingLocale / useBookingT) wire these to the route, the
// page data and the browser; every rule about WHICH locale is decided here.
//
// The 32 codes are the app's own ARB codes (Workspace369/lib/l10n/app_<code>.arb),
// so a page's `default_language` and a booking's `client_locale` mean the same
// thing on every side. Two of them are not BCP-47 (`pt_PT`, `zh_TW`); the
// `bookingLocaleBcp47` map is what Intl and `<html lang>` get.

/** The app's ARB codes, in the app's order. */
export const BOOKING_LOCALES = [
  'ar', 'cs', 'da', 'de', 'el', 'en', 'es', 'fi', 'fr', 'he', 'hu', 'id', 'is', 'it', 'ja', 'ko',
  'ms', 'nb', 'nl', 'pl', 'pt', 'pt_PT', 'ro', 'ru', 'sv', 'th', 'tl', 'tr', 'uk', 'vi', 'zh', 'zh_TW',
] as const

export type BookingLocale = (typeof BOOKING_LOCALES)[number]

export const DEFAULT_BOOKING_LOCALE: BookingLocale = 'en'

/** The two right-to-left locales. `<html dir>` follows this. */
export const BOOKING_RTL_LOCALES: ReadonlySet<BookingLocale> = new Set<BookingLocale>(['ar', 'he'])

export type BookingTextDirection = 'ltr' | 'rtl'

const BCP47_OVERRIDES: Partial<Record<BookingLocale, string>> = {
  pt_PT: 'pt-PT',
  zh_TW: 'zh-TW',
}

/** The BCP-47 tag for a code: `pt_PT` → `pt-PT`, `zh_TW` → `zh-TW`, the rest unchanged. */
export function bookingLocaleBcp47(locale: BookingLocale): string {
  return BCP47_OVERRIDES[locale] ?? locale
}

export function bookingLocaleDir(locale: BookingLocale): BookingTextDirection {
  return BOOKING_RTL_LOCALES.has(locale) ? 'rtl' : 'ltr'
}

const LOCALE_SET: ReadonlySet<string> = new Set<string>(BOOKING_LOCALES)

/** Strict membership: the exact ARB code, nothing normalised. */
export function isBookingLocale(value: unknown): value is BookingLocale {
  return typeof value === 'string' && LOCALE_SET.has(value)
}

/**
 * Languages that reach us under another name. Legacy ISO codes some browsers
 * still send (`iw` Hebrew, `in` Indonesian), the Norwegian macro-language and
 * Nynorsk (both served Bokmål), and `fil` for Filipino, which the app files
 * under `tl`.
 */
const LANGUAGE_ALIASES: Record<string, BookingLocale> = {
  iw: 'he',
  in: 'id',
  no: 'nb',
  nn: 'nb',
  fil: 'tl',
}

/** Regions (and the Traditional script) whose Chinese is the Taiwan file. */
const CHINESE_TRADITIONAL_SUBTAGS = new Set(['hant', 'tw', 'hk', 'mo'])

/**
 * One BCP-47 tag → the locale we ship for it, or `null`.
 *
 * Exact first (case-insensitive, `_` and `-` alike: `pt-PT`, `pt_pt` and
 * `PT-PT` are all `pt_PT`), then the language alone (`pt-BR` → `pt`, `de-CH`
 * → `de`). Chinese is the one language where the language alone is not
 * enough: `zh-Hant`, `zh-TW`, `zh-HK` and `zh-MO` read as `zh_TW`, everything
 * else under `zh` as `zh`.
 */
export function matchBookingLocale(tag: unknown): BookingLocale | null {
  if (typeof tag !== 'string') return null
  const normalized = tag.trim().toLowerCase().replace(/_/g, '-')
  if (!normalized) return null
  const subtags = normalized.split('-').filter(Boolean)
  const language = subtags[0]
  if (!language) return null

  if (language === 'zh') {
    return subtags.slice(1).some(subtag => CHINESE_TRADITIONAL_SUBTAGS.has(subtag)) ? 'zh_TW' : 'zh'
  }
  if (subtags.length >= 2) {
    const exact = `${language}_${subtags[1]!.toUpperCase()}`
    if (isBookingLocale(exact)) return exact
  }
  if (isBookingLocale(language)) return language
  return LANGUAGE_ALIASES[language] ?? null
}

/**
 * An `Accept-Language` header as an ordered list of tags, highest `q` first
 * (header order breaks ties); `*` and `q=0` entries are dropped. What the
 * server-side render feeds the resolver in place of `navigator.languages`.
 */
export function parseAcceptLanguage(header: unknown): string[] {
  if (typeof header !== 'string' || !header.trim()) return []
  const entries: Array<{ tag: string, q: number, index: number }> = []
  header.split(',').forEach((part, index) => {
    const [rawTag = '', ...params] = part.split(';')
    const tag = rawTag.trim()
    if (!tag || tag === '*') return
    let q = 1
    for (const param of params) {
      const [name = '', value = ''] = param.split('=')
      if (name.trim().toLowerCase() !== 'q') continue
      const parsed = Number.parseFloat(value.trim())
      q = Number.isFinite(parsed) ? parsed : 0
    }
    if (q <= 0) return
    entries.push({ tag, q, index })
  })
  return entries
    .sort((a, b) => b.q - a.q || a.index - b.index)
    .map(entry => entry.tag)
}

export interface ResolveBookingLocaleInput {
  /** The page URL's `lang` query (the embed maps `data-lang` / `mount({ lang })` onto it). */
  queryLang?: unknown
  /** The page's `default_language`: `"auto"` (or absent) defers to the browser. */
  pageDefault?: unknown
  /** `navigator.languages` on the client; the parsed `Accept-Language` on the server. */
  navigatorLanguages?: readonly string[] | null
}

/**
 * Contract C1: (1) the `lang` query, (2) the page's default when it is not
 * `"auto"`, (3) the visitor's languages in order, each tried exact then
 * language-only, (4) English. A value at any step that names no locale we
 * ship is skipped, not read as English — a typo in `data-lang` should not
 * turn a Polish page's Polish visitor English.
 */
export function resolveBookingLocale(input: ResolveBookingLocaleInput): BookingLocale {
  const fromQuery = matchBookingLocale(input.queryLang)
  if (fromQuery) return fromQuery

  const pageDefault = typeof input.pageDefault === 'string' ? input.pageDefault.trim() : ''
  if (pageDefault && pageDefault.toLowerCase() !== 'auto') {
    const fromPage = matchBookingLocale(pageDefault)
    if (fromPage) return fromPage
  }

  for (const tag of input.navigatorLanguages ?? []) {
    const matched = matchBookingLocale(tag)
    if (matched) return matched
  }

  return DEFAULT_BOOKING_LOCALE
}
